import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { Archive, ChevronLeft, ExternalLink, Globe, KeyRound, ListChecks, Newspaper, ScrollText, SearchCheck, Settings2 } from 'lucide-react';
import { Card, MarketName, PageHeader, PlatformName, Skeleton, StatusPill, TableSkeleton, TabPanel, Tabs } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { ActivityTab } from '@/components/sites/ActivityTab';
import { BackupsTab } from '@/components/sites/BackupsTab';
import { BootstrapCredentialBanner } from '@/components/sites/BootstrapCredential';
import { CredentialsTab } from '@/components/sites/CredentialsTab';
import { SeoTab } from '@/components/sites/SeoTab';
import { ContentTab } from '@/components/sites/ContentTab';
import { DomainsTab } from '@/components/sites/DomainsTab';
import { HistoryTab } from '@/components/sites/HistoryTab';
import { OverviewTab } from '@/components/sites/OverviewTab';
import { SiteActions } from '@/components/sites/SiteActions';
import { SiteStateBanner } from '@/components/sites/SiteStateBanner';
import { ZoneSslBanner } from '@/components/sites/ZoneSsl';
import { useRoles } from '@/hooks/useAuth';
import { useSiteJobs } from '@/hooks/useJobs';
import { useRefreshOnFinishedJobs, useSite, useSiteBackups } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { label, SITE_HEALTH, SITE_STATUS, sitePlatform, TIER_LABELS } from '@/utils/labels';
import { siteCapabilities } from '@/utils/siteCapabilities';
import { siteUrl } from '@/utils/siteUrl';
import type { SiteDetail } from '@/types/api';

/**
 * One site, in full.
 *
 * Tabs rather than a column of cards: a site has five unrelated kinds of
 * information, and stacking them means scrolling past four to reach the fifth.
 * The header carries what is true right now — state, health, domain — because
 * that is what someone opening this page during an incident came for.
 */

type Tab = 'overview' | 'domains' | 'seo' | 'content' | 'backups' | 'credentials' | 'history' | 'activity';

export function SiteDetailPage(): ReactNode {
  const { id = '' } = useParams();
  const roles = useRoles();
  // Opened on its SEO tab from the network's SEO page (ADR-0045).
  const [params] = useSearchParams();
  // …and on its Content tab from the Content page (ADR-0051).
  const opened = params.get('tab');
  const [tab, setTab] = useState<Tab>(opened === 'seo' || opened === 'content' ? opened : 'overview');

  const site = useSite(id);
  // The newest jobs, for what the page has to know now: a removal or a move in
  // progress, a creation that failed. The History tab pages through the rest.
  const jobs = useSiteJobs(id);
  const recentJobs = jobs.data?.items ?? [];
  const backups = useSiteBackups(id);
  useRefreshOnFinishedJobs(id, recentJobs);

  // A queued removal is followed here, so the page does not sit showing a site
  // that no longer exists: once the job completes, back to the list.
  const navigate = useNavigate();
  const removal = recentJobs.find((job) => job.type === 'site.destroy' && job.state !== 'failed' && job.state !== 'cancelled');
  const removed = removal?.state === 'completed';
  const removedName = site.data?.displayName;
  useEffect(() => {
    if (!removed) return;
    toast.success('Сайт удалён', removedName);
    navigate('/sites', { replace: true });
  }, [removed, removedName, navigate]);

  if (site.isPending) return <SiteSkeleton />;

  if (site.isError) {
    return (
      <div className="page">
        <BackLink />
        <ErrorState error={site.error} fallback="Сайт не найден" />
      </div>
    );
  }

  // While a removal is queued the site is, for every purpose on this page,
  // being removed: no stale "Ошибка" pill, no second delete button.
  const data: SiteDetail = removal ? { ...site.data, status: 'destroying' } : site.data;
  const may = siteCapabilities(data, roles);
  const platform = sitePlatform(data);

  return (
    <div className="page">
      <div>
        <BackLink />

        <PageHeader title={data.displayName} actions={<SiteActions site={data} may={may} />} />

        <div className="row-tight row-wrap" style={{ marginTop: 'var(--space-3)' }}>
          {data.status === 'ready' ? null : <StatusPill map={SITE_STATUS} status={data.status} />}
          <StatusPill map={SITE_HEALTH} status={data.healthStatus} />
          <span className="tag">
            <PlatformName platform={platform.key} label={platform.label} size={12} />
          </span>
          {data.country || data.language ? (
            <span className="tag">
              <MarketName country={data.country} language={data.language} codes={false} />
            </span>
          ) : null}
          {data.tier === 'staging' ? <span className="tag">{label(TIER_LABELS, data.tier)}</span> : null}
          {may.observed ? <span className="tag">{data.hostName ? `Мониторинг · ${data.hostName}` : 'Мониторинг'}</span> : null}
          {data.templateBuild ? (
            <Link className="tag tag-link" to="/templates">
              Шаблон «{data.templateBuild.name}» · {data.templateBuild.version}
            </Link>
          ) : null}
          {data.referenceOf ? (
            <Link className="tag tag-link" to="/templates">
              Эталон шаблона «{data.referenceOf.name}»
            </Link>
          ) : null}
          {data.launchPending ? <span className="tag tag-warning">Закрыт от поисковиков</span> : null}
          {data.primaryDomain ? (
            <a className="mono link-external" href={siteUrl(data.primaryDomain)} target="_blank" rel="noreferrer noopener">
              {data.primaryDomain}
              <ExternalLink size={12} aria-hidden="true" />
            </a>
          ) : null}
          {data.adminUrl ? (
            <a className="link-external" href={data.adminUrl} target="_blank" rel="noreferrer noopener">
              Админка
              <ExternalLink size={12} aria-hidden="true" />
            </a>
          ) : null}
        </div>
      </div>

      <SiteStateBanner site={data} jobs={recentJobs} />

      <ZoneSslBanner site={data} />

      {may.revealCredential ? <BootstrapCredentialBanner site={data} /> : null}

      <Tabs
        label="Разделы сайта"
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'overview', label: 'Обзор', icon: Settings2 },
          { id: 'domains', label: 'Домены', icon: Globe, count: data.domains.length },
          ...(data.primaryDomain ? [{ id: 'seo' as const, label: 'SEO', icon: SearchCheck }] : []),
          ...(data.primaryDomain ? [{ id: 'content' as const, label: 'Контент', icon: Newspaper }] : []),
          { id: 'backups', label: 'Копии', icon: Archive, count: backups.data?.pages[0]?.total ?? data.backups.length },
          ...(may.credentials ? [{ id: 'credentials' as const, label: 'Доступы', icon: KeyRound }] : []),
          { id: 'history', label: 'История', icon: ListChecks, count: jobs.data?.total ?? 0 },
          { id: 'activity', label: 'Журнал', icon: ScrollText },
        ]}
      />

      <TabPanel id="overview" active={tab}>
        <OverviewTab site={data} may={may} />
      </TabPanel>

      <TabPanel id="domains" active={tab}>
        <DomainsTab site={data} mayEdit={may.editDomains} mayManageDns={may.manageDns} />
      </TabPanel>

      {data.primaryDomain ? (
        <TabPanel id="seo" active={tab}>
          <SeoTab site={data} mayEdit={may.editPlatform} />
        </TabPanel>
      ) : null}

      {data.primaryDomain ? (
        <TabPanel id="content" active={tab}>
          <ContentTab site={data} />
        </TabPanel>
      ) : null}

      <TabPanel id="backups" active={tab}>
        <BackupsTab site={data} mayRestore={may.restore} query={backups} />
      </TabPanel>

      {may.credentials ? (
        <TabPanel id="credentials" active={tab}>
          <CredentialsTab owner={{ kind: 'site', id: data.id }} site={{ engine: data.engine, platform: data.platformInfo?.name ?? null, adminUrl: data.adminUrl }} />
        </TabPanel>
      ) : null}

      <TabPanel id="history" active={tab}>
        <HistoryTab siteId={id} />
      </TabPanel>

      <TabPanel id="activity" active={tab}>
        <ActivityTab siteId={id} />
      </TabPanel>
    </div>
  );
}

function BackLink(): ReactNode {
  return (
    <Link to="/sites" className="back-link">
      <ChevronLeft size={14} aria-hidden="true" />К списку сайтов
    </Link>
  );
}

function SiteSkeleton(): ReactNode {
  return (
    <div className="page">
      <Skeleton width={140} height={13} />
      <Skeleton width={320} height={30} radius="md" />
      <Skeleton width="100%" height={38} radius="md" />
      <Card flush>
        <TableSkeleton rows={4} columns={3} />
      </Card>
    </div>
  );
}
